import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

interface PermissionContextType {
  permissions: Record<string, string[]>;
  hasPermission: (module: string, action?: string) => boolean;
  isModuleVisible: (moduleName: string) => boolean;
  refreshPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [permissions, setPermissions] = useState<Record<string, string[]>>(() => {
    try {
      const currentUser = localStorage.getItem("currentUser");
      if (currentUser) {
        const parsed = JSON.parse(currentUser);
        if (parsed && parsed.rights) return parsed.rights;
      }
    } catch (e) {
      console.error("Initial permission load failed:", e);
    }
    return {};
  });

  const [allowedModulesRaw, setAllowedModulesRaw] = useState<any[]>(() => {
    try {
      const currentUser = localStorage.getItem("currentUser");
      if (currentUser) {
        const parsed = JSON.parse(currentUser);
        const modulesArray = parsed.allowed_modules;
        if (parsed && Array.isArray(modulesArray)) {
          return modulesArray;
        }
      }
    } catch (e) {
      console.error("Initial allowed_modules raw load failed:", e);
    }
    return [];
  });

  const loadPermissions = () => {
    try {
      const currentUser = localStorage.getItem("currentUser");
      if (currentUser) {
        const parsed = JSON.parse(currentUser);
        if (parsed && parsed.rights) {
          setPermissions(parsed.rights);
        } else {
          setPermissions({});
        }
        const modulesArray = parsed.allowed_modules;
        if (parsed && Array.isArray(modulesArray)) {
          setAllowedModulesRaw(modulesArray);
        } else {
          setAllowedModulesRaw([]);
        }
        return;
      }
      setPermissions({});
      setAllowedModulesRaw([]);
    } catch (error) {
      console.error("Failed to parse permissions from localStorage:", error);
      setPermissions({});
      setAllowedModulesRaw([]);
    }
  };

  const hasPermission = (module: string, action?: string): boolean => {
    if (!permissions || !module) return false;

    const modules = module.split(",").map((m) => m.trim());

    return modules.some((mod) => {
      const [actualMod, actionPrefix] = mod.split(":");
      const moduleRights = permissions[actualMod];
      if (!moduleRights) return false;

      if (!action) return true;

      const actions = action.split(",").map((act) => act.trim());
      return actions.some((act) => {
        const mappedAction = actionPrefix ? `${actionPrefix}-${act}` : act;
        return moduleRights.includes(mappedAction);
      });
    });
  };

 // Derive the set of active feature codes/names recursively
  const activeFeaturesSet = useMemo(() => {
    const activeKeys = new Set<string>();
    if (!allowedModulesRaw || allowedModulesRaw.length === 0) {
      return activeKeys;
    }

    const traverse = (mod: any) => {
      if (!mod) return;
      if (mod.isenabled === false) return;

      if (mod.featurecode) {
        activeKeys.add(mod.featurecode.toLowerCase().trim());
      }
      if (mod.featurename) {
        activeKeys.add(mod.featurename.toLowerCase().trim());
      }

      if (Array.isArray(mod.dependentfeatures)) {
        mod.dependentfeatures.forEach((dep: any) => {
          traverse(dep);
        });
      }
    };

    allowedModulesRaw.forEach((mod) => traverse(mod));
    return activeKeys;
  }, [allowedModulesRaw]);

  const isModuleVisible = (moduleIdentifier: string): boolean => {
    if (!moduleIdentifier) return true;
    const alwaysAllowedModule = ["global study access"];

   if(moduleIdentifier && alwaysAllowedModule .includes(moduleIdentifier.toLowerCase().trim())){
    return true;
   }
    return checkRawModuleEnabled(moduleIdentifier);
  };

  const checkRawModuleEnabled = (moduleIdentifier: string): boolean => {
    if (!allowedModulesRaw || allowedModulesRaw.length === 0) {
      return true;
    }

    const searchKey = moduleIdentifier.toLowerCase().trim();
    return activeFeaturesSet.has(searchKey);
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        hasPermission,
        isModuleVisible,
        refreshPermissions: loadPermissions
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error("usePermission must be used within a PermissionProvider");
  }
  return context;
};
